import React, { useEffect, useState } from "react";
import api, { errText } from "@/lib/api";
import { toast } from "sonner";
import { UserPlus, X } from "lucide-react";

export default function Customers() {
  const [tab, setTab] = useState("customers");
  const [customers, setCustomers] = useState([]);
  const [renewals, setRenewals] = useState(null);
  const [show, setShow] = useState(false);
  const [nc, setNc] = useState({ name: "", phone: "", email: "", address: "", products: "" });

  const load = () => {
    api.get("/customers").then((r) => setCustomers(r.data));
    api.get("/renewals").then((r) => setRenewals(r.data));
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    try {
      await api.post("/customers", { ...nc, products: nc.products.split(",").map((s) => s.trim()).filter(Boolean) });
      setShow(false); setNc({ name: "", phone: "", email: "", address: "", products: "" }); load();
      toast.success("Customer created");
    } catch (e) { toast.error(errText(e.response?.data?.detail)); }
  };
  const input = "w-full h-11 px-3 rounded-sm border border-slate-300 focus:outline-none focus:ring-2 focus:ring-[#C5A059]";
  const buckets = [["overdue", "Overdue / Expired", "text-red-600"], ["this_week", "This Week", "text-[#C5A059]"], ["in_30", "In 30 Days", ""], ["in_60", "In 60 Days", ""], ["in_90", "In 90 Days", ""]];

  return (
    <div className="p-4 sm:p-6">
      <div className="flex items-center justify-between mb-4">
        <h1 className="font-display text-3xl font-bold">Customers</h1>
        <button onClick={() => setShow(true)} className="bg-[#0B1E36] text-white font-semibold px-4 h-10 rounded-sm flex items-center gap-2 text-sm" data-testid="add-customer-btn"><UserPlus size={16} /> Add Customer</button>
      </div>
      <div className="flex gap-2 mb-5">
        {["customers", "renewals"].map((t) => (
          <button key={t} onClick={() => setTab(t)} className={`px-4 py-2 rounded-sm text-sm font-semibold capitalize ${tab === t ? "bg-[#C5A059] text-[#0B1E36]" : "bg-white border border-slate-200"}`} data-testid={`tab-${t}`}>{t}</button>
        ))}
      </div>

      {tab === "customers" && (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {customers.map((c) => (
            <div key={c.id} className="bg-white border border-slate-200 rounded-sm p-5" data-testid={`customer-${c.id}`}>
              <h3 className="font-display text-lg font-bold">{c.name}</h3>
              <p className="text-sm text-slate-600">{c.phone} · {c.email}</p>
              <p className="text-sm text-slate-500 mt-1">{c.address}</p>
              <div className="mt-3 flex flex-wrap gap-1">{(c.products || []).map((p) => <span key={p} className="text-xs bg-slate-100 rounded px-2 py-0.5">{p}</span>)}</div>
              <p className="text-xs text-slate-500 mt-3">{(c.policies || []).length} polic{(c.policies || []).length === 1 ? "y" : "ies"}</p>
              {c.cross_sell?.length > 0 && (
                <div className="mt-3 border-t border-slate-100 pt-3">
                  {c.cross_sell.map((x) => <div key={x} className="text-xs font-bold text-[#C5A059]" data-testid={`crosssell-${c.id}`}>CROSS-SELL: {x}</div>)}
                </div>
              )}
            </div>
          ))}
          {customers.length === 0 && <p className="text-slate-400">No customers yet. Convert a lead or add one manually.</p>}
        </div>
      )}

      {tab === "renewals" && renewals && (
        <div className="space-y-5">
          {buckets.map(([key, title, color]) => (
            <div key={key} className="bg-white border border-slate-200 rounded-sm p-5" data-testid={`renewal-${key}`}>
              <h3 className={`font-display text-lg font-bold ${color}`}>{title} <span className="font-mono text-sm">({renewals[key].length})</span></h3>
              <div className="mt-3 space-y-1">
                {renewals[key].length === 0 ? <p className="text-sm text-slate-400">None.</p> :
                  renewals[key].map((r, i) => <div key={i} className="flex justify-between text-sm border-b border-slate-100 py-2"><span className="font-medium">{r.customer} · {r.product || r.carrier}</span><span className="text-slate-500">{new Date(r.expiration_date).toLocaleDateString()} ({r.days}d)</span></div>)}
              </div>
            </div>
          ))}
        </div>
      )}

      {show && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShow(false)}>
          <div className="bg-white rounded-sm p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4"><h3 className="font-display text-xl font-bold">New Customer</h3><button onClick={() => setShow(false)}><X /></button></div>
            <div className="space-y-3">
              <input className={input} placeholder="Full Name" value={nc.name} onChange={(e) => setNc({ ...nc, name: e.target.value })} data-testid="nc-name" />
              <input className={input} placeholder="Phone" value={nc.phone} onChange={(e) => setNc({ ...nc, phone: e.target.value })} data-testid="nc-phone" />
              <input className={input} placeholder="Email" value={nc.email} onChange={(e) => setNc({ ...nc, email: e.target.value })} data-testid="nc-email" />
              <input className={input} placeholder="Address" value={nc.address} onChange={(e) => setNc({ ...nc, address: e.target.value })} data-testid="nc-address" />
              <input className={input} placeholder="Products (comma separated e.g. Auto, Home)" value={nc.products} onChange={(e) => setNc({ ...nc, products: e.target.value })} data-testid="nc-products" />
              <button onClick={create} className="w-full bg-[#0B1E36] text-white font-bold h-11 rounded-sm" data-testid="nc-save">Create Customer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
